import { formatToDollar } from "@inrev/common";
import { Option } from "@inrev/core";
import type { ReactNode } from "react";
import { formatEmpty } from "../../../utils";
import { MetricCard } from "../../patterns/metric-card";
import { ClickableIcon } from "../../primitives";
import type { Delta } from "../calculateDelta";

export type FinancialMetricCardProps = {
	testId?: string;
	label: string;
	value: Option<string>;
	delta?: Option<Delta>;
	additionalInfo?: ReactNode;
	onEditClick?: () => void;
};

const renderTrend = (delta: Option<Delta>): ReactNode => {
	if (Option.isNone(delta)) {
		return null;
	}
	if (delta.type === "amount") {
		return (
			<MetricCard.Trend direction={delta.direction}>
				{`${formatToDollar(Math.abs(delta.amount), { abbreviate: true })} YoY`}
			</MetricCard.Trend>
		);
	}
	if (delta.percent !== 0) {
		return (
			<MetricCard.Trend
				className="tabular-nums"
				direction={delta.direction}
			>{`${Math.abs(delta.percent)}% YoY`}</MetricCard.Trend>
		);
	}
	return null;
};

export const FinancialMetricCard = ({
	testId,
	label,
	value,
	delta,
	additionalInfo,
	onEditClick,
}: FinancialMetricCardProps) => {
	return (
		<MetricCard
			data-testid={testId}
			action={
				onEditClick ? (
					<ClickableIcon
						data-testid={testId ? `${testId}-edit-action` : undefined}
						type="pencil"
						size="micro"
						onClick={onEditClick}
					/>
				) : undefined
			}
		>
			<MetricCard.Label>{label}</MetricCard.Label>
			<MetricCard.ValueRow>
				<MetricCard.Value className="tabular-nums">{formatEmpty(value)}</MetricCard.Value>
				{renderTrend(delta)}
			</MetricCard.ValueRow>
			{additionalInfo && (
				<MetricCard.Caption className="tabular-nums">{additionalInfo}</MetricCard.Caption>
			)}
		</MetricCard>
	);
};
